import React, { useState, useEffect, useCallback, useId } from 'react';
import { X, Calculator, SlidersHorizontal, Wind, Weight, Diameter, ArrowDownUp, Save, ChevronsRight, Copy, Info, GitCommitVertical, Eraser, Plus, Trash2, ChevronDown, ChevronUp, Ruler } from 'lucide-react';

// Component for a single input field in the parameters tab
const ParamInput = ({ label, unit, value, onChange, icon: Icon, onFocus, ...props }) => {
    const inputId = useId();
    return (
        <div className="flex flex-col">
            <label htmlFor={inputId} className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1 flex items-center cursor-pointer w-fit">
                {Icon && <Icon className="h-4 w-4 mr-2 text-slate-500" />}
                {label}
            </label>
            <div className="relative">
                <input
                    id={inputId}
                    type="number"
                    value={value}
                    onChange={onChange}
                    onFocus={onFocus}
                    className="input-style w-full pr-16 pl-4 text-right"
                    {...props}
                />
                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-slate-500 dark:text-slate-400 pointer-events-none">
                    {unit}
                </span>
            </div>
        </div>
    );
};

// Compact labelled input used inside the span cards (Modo Digitado)
const MiniInput = ({ label, hint, labelClassName = '', ...props }) => {
    const inputId = useId();
    return (
        <div className="flex flex-col">
            <label htmlFor={inputId} className={`text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 truncate ${labelClassName}`} title={hint || label}>
                {label}
            </label>
            <input
                id={inputId}
                type="number"
                className="input-style w-full px-2 py-1.5 text-right text-sm"
                onFocus={(e) => e.target.select()}
                {...props}
            />
        </div>
    );
};

// Component for button groups in "Padrão" mode
const StandardButtonGroup = ({ label, options, selected, onSelect, gridClass = 'grid-cols-4' }) => {
    const groupId = useId();
    return (
        <div role="radiogroup" aria-labelledby={groupId}>
            <label id={groupId} className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block">{label}</label>
            <div className={`grid ${gridClass} gap-2`}>
                {options.map(option => (
                    <button
                        key={option}
                        type="button"
                        role="radio"
                        aria-checked={selected === option}
                        onClick={() => onSelect(option)}
                        className={`w-full px-2 py-2 rounded-md text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800 ${
                            selected === option
                                ? 'bg-blue-600 text-white shadow'
                                : 'bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600'
                        }`}
                    >
                        {option}
                    </button>
                ))}
            </div>
        </div>
    );
};

// Stable ids for the span/cable lists (React keys + focus stability while editing).
let uidCounter = 0;
const nextId = (prefix) => `${prefix}-${++uidCounter}`;

// A cable line inside a span. Empty `diameter` falls back to the global parameter,
// empty `spanLength` means the cable runs the full length of the span.
const createCable = (weight = '') => ({
    id: nextId('cable'),
    qty: '1',
    weight: String(weight ?? ''),
    diameter: '',
    spanLength: '',
});

const createSpan = (weight = '') => ({
    id: nextId('span'),
    length: '',
    angle: '',
    detailed: false,
    cables: [createCable(weight)],
});

const DEFAULT_PARAMS = {
    windSpeed: '40',
    cableWeight: '82',
    cableDiameter: '11',
    messengerWeight: '30',
    arrow: '1.5',
    // Altura em que os cabos são fixados no poste. Fr converte o momento gerado
    // nessa altura para o ponto de esforço nominal, a 0,2 m do topo.
    attachmentHeight: '5.5',
};

const buildInitialCalcInput = (weight = '') => ({
    postHeight: '12',
    analysisPoint: '0.2',
    useMessenger: false, // CORREÇÃO: Padrão alterado para 'false' para corresponder à planilha.
    spans: [{ ...createSpan(weight), length: '40' }],
});

const EffortCalculatorModal = ({ onClose }) => {
    const [activeTab, setActiveTab] = useState('calculo');
    const [params, setParams] = useState(DEFAULT_PARAMS);
    const [showSuccess, setShowSuccess] = useState(false);
    const [calcMode, setCalcMode] = useState('digitado'); // 'digitado' or 'padrao'
    const [calcInput, setCalcInput] = useState(() => buildInitialCalcInput('82'));
    const [standardInput, setStandardInput] = useState({
        span: '',
        postHeight: '',
        cableQty: '',
        useMessenger: false,
    });
    const [result, setResult] = useState(null); // Horizontal Tension (H)
    const [forceAtPoint, setForceAtPoint] = useState(null); // Resultant force at point (Fr)
    const [resultantAngle, setResultantAngle] = useState(null);
    const [breakdown, setBreakdown] = useState([]); // Per-span contribution, for verification
    const [error, setError] = useState('');
    const [copySuccess, setCopySuccess] = useState('');

    // Load parameters from localStorage on mount
    useEffect(() => {
        try {
            const savedParams = localStorage.getItem('effortCalculatorParams');
            if (savedParams) {
                // Mesclado sobre os defaults: parâmetros salvos antes da introdução de um
                // campo novo não o trazem, e sobrescrever o objeto inteiro o deixaria vazio.
                const parsedParams = { ...DEFAULT_PARAMS, ...JSON.parse(savedParams) };
                setParams(parsedParams);
                setCalcInput(prev => ({
                    ...prev,
                    spans: prev.spans.map(span => ({
                        ...span,
                        cables: span.cables.map(cable => ({ ...cable, weight: String(parsedParams.cableWeight ?? '') })),
                    })),
                }));
            }
        } catch (error) {
            console.error("Failed to parse parameters from localStorage", error);
        }
    }, []);

    // Main calculation logic
    const calculateEffort = useCallback(() => {
        const clearResults = () => {
            setResult(null);
            setForceAtPoint(null);
            setResultantAngle(null);
            setBreakdown([]);
        };
        const fail = (message, tab) => {
            setError(message);
            if (tab) setActiveTab(tab);
            clearResults();
        };

        const p = {
            windSpeed: parseFloat(params.windSpeed),
            cableWeight: parseFloat(params.cableWeight),
            cableDiameter: parseFloat(params.cableDiameter),
            messengerWeight: parseFloat(params.messengerWeight),
            arrow: parseFloat(params.arrow),
            attachmentHeight: parseFloat(params.attachmentHeight),
        };

        if (Object.values(p).some(isNaN)) {
            return fail('Todos os "Parâmetros" devem ser preenchidos.', 'parametros');
        }
        if (p.arrow <= 0) {
            return fail('A flecha (%) deve ser maior que zero.', 'parametros');
        }
        if (p.attachmentHeight <= 0) {
            return fail('A altura de fixação dos cabos deve ser maior que zero.', 'parametros');
        }

        const isDigitado = calcMode === 'digitado';

        // Both modes feed the same engine: "Padrão" is just a single span with N identical,
        // full-length cables using the global cable weight/diameter.
        const spans = isDigitado ? calcInput.spans : [{
            id: 'padrao',
            length: String(standardInput.span),
            angle: '',
            detailed: false,
            cables: [{ id: 'padrao-cable', qty: String(standardInput.cableQty), weight: params.cableWeight, diameter: '', spanLength: '' }],
        }];

        const postHeight = parseFloat(isDigitado ? calcInput.postHeight : standardInput.postHeight);
        const analysisPoint = parseFloat(isDigitado ? calcInput.analysisPoint : '0.2');
        const useMessenger = isDigitado ? calcInput.useMessenger : standardInput.useMessenger;

        if (isNaN(postHeight) || postHeight <= 0) {
            return fail('Altura do Poste deve ser um valor positivo.');
        }
        if (isNaN(analysisPoint) || analysisPoint < 0) {
            return fail('Ponto de Análise deve ser zero ou positivo.');
        }

        // Altura, acima do solo, do ponto onde se especifica o esforço nominal do poste:
        // altura total menos o engastamento (10% do comprimento + 0,6 m) menos o ponto de análise.
        const leverArm = postHeight - (postHeight * 0.1 + 0.6) - analysisPoint;
        if (leverArm <= 0) {
            return fail('Altura do Poste e Ponto de Análise não deixam altura útil para o cálculo da resultante.');
        }

        const sagFraction = p.arrow / 100;
        const vectors = [];

        for (let index = 0; index < spans.length; index += 1) {
            const span = spans[index];
            const label = isDigitado ? `Vão ${index + 1}` : 'Vão';
            const L = parseFloat(span.length);

            if (isNaN(L) || L <= 0) {
                return fail(`Comprimento do ${label} deve ser um valor positivo.`);
            }

            // Span 1 is the reference direction (0°); every other span is measured against it.
            let angleDeg = 0;
            if (index > 0) {
                angleDeg = parseFloat(span.angle);
                if (isNaN(angleDeg)) {
                    return fail(`Informe o ângulo do ${label} em relação ao Vão 1.`);
                }
                if (angleDeg < 0 || angleDeg > 360) {
                    return fail(`Ângulo do ${label} deve estar entre 0° e 360°.`);
                }
            }

            if (!span.cables.length) {
                return fail(`${label} precisa de ao menos um cabo.`);
            }

            // Load accumulated over the span, each cable weighted by the fraction of the
            // span it actually occupies (ℓ/L). Because the sag is a percentage of the span,
            // H is linear in length — so a cable running only ℓ metres contributes exactly
            // ℓ/L of a full-length cable. Cables at ℓ = L reproduce the original formula.
            let windSum = 0;   // Σ dᵢ · nᵢ · (ℓᵢ/L)  [mm]
            let weightSum = 0; // Σ wᵢ · nᵢ · (ℓᵢ/L)  [kg/km]

            for (const cable of span.cables) {
                const qty = parseInt(cable.qty, 10);
                const weight = parseFloat(cable.weight);
                const diameter = cable.diameter === '' || cable.diameter === null || cable.diameter === undefined
                    ? p.cableDiameter
                    : parseFloat(cable.diameter);
                const rawLength = cable.spanLength === '' || cable.spanLength === null || cable.spanLength === undefined
                    ? L
                    : parseFloat(cable.spanLength);

                if (isNaN(qty) || qty <= 0) {
                    return fail(`Quantidade de cabos do ${label} deve ser um inteiro positivo.`);
                }
                if (isNaN(weight) || weight <= 0) {
                    return fail(`Peso do cabo do ${label} deve ser um valor positivo.`);
                }
                if (isNaN(diameter) || diameter <= 0) {
                    return fail(`Diâmetro do cabo do ${label} deve ser um valor positivo.`);
                }
                if (isNaN(rawLength) || rawLength <= 0) {
                    return fail(`Comprimento do cabo no ${label} deve ser um valor positivo.`);
                }

                const ratio = Math.min(rawLength, L) / L;
                windSum += diameter * qty * ratio;
                weightSum += weight * qty * ratio;
            }

            const windForcePerMeter = (0.0011664 * p.windSpeed ** 2 * windSum) / 1000;
            // The messenger runs the whole span, so it is counted once at full length.
            const totalWeightPerKm = useMessenger ? weightSum + p.messengerWeight : weightSum;
            const verticalForcePerMeter = totalWeightPerKm / 1000;

            const resultantForcePerMeter = Math.sqrt(windForcePerMeter ** 2 + verticalForcePerMeter ** 2);
            const sagMeters = L * sagFraction;
            const tensionH = (resultantForcePerMeter * L ** 2) / (8 * sagMeters);
            // Equivalência de momento na base: H aplicado na altura de fixação produz o
            // mesmo momento que Fr aplicado no ponto de esforço nominal do poste.
            const forceAtPointSpan = tensionH * p.attachmentHeight / leverArm;

            const angleRad = angleDeg * (Math.PI / 180);
            vectors.push({
                label,
                angleDeg,
                H: tensionH,
                Fr: forceAtPointSpan,
                ux: Math.cos(angleRad),
                uy: Math.sin(angleRad),
            });
        }

        // Vector sum of every span pulling on the pole. For two spans this is algebraically
        // identical to the law of cosines used before: |R|² = F₁² + F₂² + 2·F₁·F₂·cos(θ).
        const Hx = vectors.reduce((acc, v) => acc + v.H * v.ux, 0);
        const Hy = vectors.reduce((acc, v) => acc + v.H * v.uy, 0);
        const Frx = vectors.reduce((acc, v) => acc + v.Fr * v.ux, 0);
        const Fry = vectors.reduce((acc, v) => acc + v.Fr * v.uy, 0);

        const resultantFr = Math.sqrt(Frx ** 2 + Fry ** 2);
        const totalFr = vectors.reduce((acc, v) => acc + v.Fr, 0);

        setError('');
        setResult(Math.sqrt(Hx ** 2 + Hy ** 2));
        setForceAtPoint(resultantFr);
        setBreakdown(vectors.map(({ label, angleDeg, H, Fr }) => ({ label, angleDeg, H, Fr })));

        // Azimuth of the resultant, measured from the Vão 1 direction. When the spans
        // cancel each other out (aligned pole), the resultant has no direction to report —
        // atan2 would only echo floating-point noise.
        if (vectors.length > 1 && totalFr > 0 && resultantFr / totalFr > 1e-4) {
            const angle = Math.atan2(Fry, Frx) * 180 / Math.PI;
            setResultantAngle((angle + 360) % 360);
        } else {
            setResultantAngle(null);
        }
    }, [params, calcMode, calcInput, standardInput]);

    // Auto-calculate for standard mode
    useEffect(() => {
        if (calcMode !== 'padrao') {
            return;
        }

        const { span, postHeight, cableQty } = standardInput;

        if (span && postHeight && cableQty) {
            calculateEffort();
        } else {
            setError('');
            setResult(null);
            setForceAtPoint(null);
            setResultantAngle(null);
            setBreakdown([]);
        }
    }, [standardInput, params, calcMode, calculateEffort]);

    // Handle parameter changes
    const handleParamChange = (e) => {
        const { name, value } = e.target;
        setParams(prev => ({ ...prev, [name]: value }));
    };

    // Save parameters to localStorage
    const saveParams = () => {
        try {
            localStorage.setItem('effortCalculatorParams', JSON.stringify(params));
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 2000); // Hide message after 2 seconds
        } catch (error)
        {
            console.error("Failed to save parameters to localStorage", error);
        }
    };

    // Handle the pole-level inputs for "Digitado" mode
    const handleCalcInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCalcInput(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const updateSpan = (spanId, patch) => {
        setCalcInput(prev => ({
            ...prev,
            spans: prev.spans.map(span => (span.id === spanId ? { ...span, ...patch } : span)),
        }));
    };

    const updateCable = (spanId, cableId, patch) => {
        setCalcInput(prev => ({
            ...prev,
            spans: prev.spans.map(span => (
                span.id === spanId
                    ? { ...span, cables: span.cables.map(cable => (cable.id === cableId ? { ...cable, ...patch } : cable)) }
                    : span
            )),
        }));
    };

    const addSpan = () => {
        setCalcInput(prev => ({ ...prev, spans: [...prev.spans, createSpan(params.cableWeight)] }));
    };

    const removeSpan = (spanId) => {
        setCalcInput(prev => ({ ...prev, spans: prev.spans.filter(span => span.id !== spanId) }));
    };

    const addCable = (spanId) => {
        setCalcInput(prev => ({
            ...prev,
            spans: prev.spans.map(span => (
                span.id === spanId
                    ? { ...span, detailed: true, cables: [...span.cables, createCable(params.cableWeight)] }
                    : span
            )),
        }));
    };

    const removeCable = (spanId, cableId) => {
        setCalcInput(prev => ({
            ...prev,
            spans: prev.spans.map(span => {
                if (span.id !== spanId || span.cables.length <= 1) return span;
                return { ...span, cables: span.cables.filter(cable => cable.id !== cableId) };
            }),
        }));
    };

    // Collapsing back to the simple view keeps only the first cable line, so what is shown
    // is always what is calculated.
    const toggleDetailed = (spanId) => {
        setCalcInput(prev => ({
            ...prev,
            spans: prev.spans.map(span => {
                if (span.id !== spanId) return span;
                if (!span.detailed) return { ...span, detailed: true };
                const [first] = span.cables;
                return { ...span, detailed: false, cables: [{ ...first, diameter: '', spanLength: '' }] };
            }),
        }));
    };

    const handleClearInputs = () => {
        setCalcInput(buildInitialCalcInput(params.cableWeight));
        setResult(null);
        setForceAtPoint(null);
        setResultantAngle(null);
        setBreakdown([]);
        setError('');
    };

    const handleFocus = (event) => {
        event.target.select();
    };

    // Handle button selections for "Padrão" mode
    const handleStandardInputChange = (name, value) => {
        setStandardInput(prev => ({ ...prev, [name]: value }));
    };

    // Handle Escape key to close modal
    useEffect(() => {
        const handleEsc = (event) => {
            if (event.keyCode === 27) {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => {
            window.removeEventListener('keydown', handleEsc);
        };
    }, [onClose]);

    const handleCopy = (text, field) => {
        if (text === null || text === undefined) return;
        navigator.clipboard.writeText(text).then(() => {
            setCopySuccess(field);
            setTimeout(() => setCopySuccess(''), 2000);
        });
    };

    const TabButton = ({ tabName, label, icon: Icon }) => (
        <button
            role="tab"
            aria-selected={activeTab === tabName}
            aria-controls={`tabpanel-${tabName}`}
            id={`tab-${tabName}`}
            onClick={() => setActiveTab(tabName)}
            className={`flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800
                ${activeTab === tabName
                    ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 border-b-2 border-blue-600'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
        >
            <Icon className="h-5 w-5" />
            <span>{label}</span>
        </button>
    );

    const ResultBox = ({ title, value, unit, field, onCopy }) => (
        <div className="bg-slate-200 dark:bg-slate-700/50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 text-center">{title}</h4>
            <div className="grid grid-cols-5 items-center gap-2">
                <div className="col-span-4 flex justify-center items-baseline">
                    <span className="text-3xl lg:text-4xl font-bold text-slate-800 dark:text-slate-100">
                        {value !== null ? value.toFixed(0) : '-'}
                    </span>
                    <span className="ml-2 text-lg text-slate-600 dark:text-slate-400 font-medium">{value !== null ? unit : ''}</span>
                </div>
                <div className="col-span-1 flex justify-end">
                    <button
                        onClick={() => onCopy(value?.toFixed(0), field)}
                        disabled={value === null}
                        className="p-2 rounded-full text-slate-500 hover:bg-slate-300 dark:text-slate-400 dark:hover:bg-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800"
                        title={`Copiar ${title}`}
                    >
                        {copySuccess === field ? <ChevronsRight className="h-5 w-5 text-green-500" /> : <Copy className="h-5 w-5" />}
                    </button>
                </div>
            </div>
        </div>
    );

    const iconButtonClass = "p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800";
    const linkButtonClass = "flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 px-2 py-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800";

    // A span is "parcial" when at least one cable does not run its whole length.
    const hasPartialCable = (span) => span.cables.some(cable => {
        const cableLength = parseFloat(cable.spanLength);
        const spanLength = parseFloat(span.length);
        return !isNaN(cableLength) && !isNaN(spanLength) && cableLength < spanLength;
    });

    return (
        <div className="fixed inset-0 bg-black/60 z-[1050] flex justify-center items-center p-4 animate-fade-in-fast">
            <div className="bg-slate-50 dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col" role="dialog" aria-modal="true">
                {/* Header */}
                <header className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
                    <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                        <Calculator className="h-6 w-6 text-blue-600" />
                        Cálculo de Esforço Mecânico
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full text-slate-500 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800"
                        aria-label="Fechar modal"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </header>

                {/* Tabs */}
                <div className="px-4 border-b border-slate-200 dark:border-slate-700">
                    <nav className="flex -mb-px" role="tablist" aria-label="Opções da calculadora">
                        <TabButton tabName="calculo" label="Cálculo" icon={Calculator} />
                        <TabButton tabName="parametros" label="Parâmetros" icon={SlidersHorizontal} />
                    </nav>
                </div>

                {/* Content */}
                <main className="p-6 overflow-y-auto">
                    {activeTab === 'calculo' && (
                        <div id="tabpanel-calculo" role="tabpanel" aria-labelledby="tab-calculo" className="animate-fade-in-fast">
                             <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                                <div className="md:col-span-8">
                                    <div className="flex justify-center mb-6">
                                        <div className="bg-slate-200 dark:bg-slate-700 p-1 rounded-full flex gap-1">
                                            {/* O estado ativo precisa declarar a cor do texto: sem ela o botão herda o
                                                preto padrão do documento e fica ilegível sobre o fundo escuro. */}
                                            <button onClick={() => setCalcMode('digitado')} className={`px-6 py-2 rounded-full text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800 ${calcMode === 'digitado' ? 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 shadow' : 'text-slate-600 dark:text-slate-300'}`}>Modo Digitado</button>
                                            <button onClick={() => setCalcMode('padrao')} className={`px-6 py-2 rounded-full text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800 ${calcMode === 'padrao' ? 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 shadow' : 'text-slate-600 dark:text-slate-300'}`}>Modo Padrão</button>
                                        </div>
                                    </div>
                                    {calcMode === 'digitado' ? (
                                        <div className="animate-fade-in-fast space-y-4">
                                            {/* Pole-level inputs, shared by every span */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                                                <ParamInput label="Altura do Poste Analisado" unit="m" name="postHeight" value={calcInput.postHeight} onChange={handleCalcInputChange} onFocus={handleFocus} placeholder="Ex: 9" />
                                                <ParamInput label="Ponto de Análise" unit="m" name="analysisPoint" value={calcInput.analysisPoint} onChange={handleCalcInputChange} onFocus={handleFocus} placeholder="Ex: 0.2" icon={GitCommitVertical} />
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-slate-600 dark:text-slate-400">
                                                    <input type="checkbox" name="useMessenger" checked={calcInput.useMessenger} onChange={handleCalcInputChange} className="h-5 w-5 rounded border-slate-300 accent-blue-600 focus:ring-blue-500"/>
                                                    Usar Cordoalha
                                                </label>
                                                <button onClick={handleClearInputs} className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors px-2 py-1 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800">
                                                    <Eraser className="h-4 w-4" />
                                                    Limpar
                                                </button>
                                            </div>

                                            {/* Spans */}
                                            <div className="space-y-3 pt-2">
                                                {calcInput.spans.map((span, index) => (
                                                    <div key={span.id} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 p-3">
                                                        <div className="flex items-center justify-between gap-2 mb-3">
                                                            <div className="flex items-center gap-2 min-w-0">
                                                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Vão {index + 1}</span>
                                                                {index === 0 && (
                                                                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 whitespace-nowrap">referência 0°</span>
                                                                )}
                                                                {hasPartialCable(span) && (
                                                                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 whitespace-nowrap">cabo parcial</span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-1 shrink-0">
                                                                <button type="button" onClick={() => toggleDetailed(span.id)} className={linkButtonClass}>
                                                                    {span.detailed ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                                                    {span.detailed ? 'Simplificar' : 'Detalhar cabos'}
                                                                </button>
                                                                {calcInput.spans.length > 1 && (
                                                                    <button type="button" onClick={() => removeSpan(span.id)} className={iconButtonClass} aria-label={`Remover Vão ${index + 1}`} title="Remover vão">
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                                            <MiniInput
                                                                label="Comprimento (m)"
                                                                value={span.length}
                                                                onChange={(e) => updateSpan(span.id, { length: e.target.value })}
                                                                placeholder="Ex: 35"
                                                            />
                                                            {index > 0 && (
                                                                <MiniInput
                                                                    label="Ângulo (°)"
                                                                    hint="Ângulo em relação ao Vão 1 (180° = poste em linha reta)"
                                                                    value={span.angle}
                                                                    onChange={(e) => updateSpan(span.id, { angle: e.target.value })}
                                                                    placeholder="Ex: 80"
                                                                />
                                                            )}
                                                            {!span.detailed && (
                                                                <>
                                                                    <MiniInput
                                                                        label="Qtd. cabos (un)"
                                                                        min="1"
                                                                        step="1"
                                                                        value={span.cables[0].qty}
                                                                        onChange={(e) => updateCable(span.id, span.cables[0].id, { qty: e.target.value })}
                                                                    />
                                                                    <MiniInput
                                                                        label="Peso (kg/km)"
                                                                        value={span.cables[0].weight}
                                                                        onChange={(e) => updateCable(span.id, span.cables[0].id, { weight: e.target.value })}
                                                                        placeholder={params.cableWeight}
                                                                    />
                                                                </>
                                                            )}
                                                        </div>

                                                        {span.detailed && (
                                                            <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                                                                <div className="hidden sm:grid grid-cols-[repeat(4,minmax(0,1fr))_2rem] gap-2 mb-1 px-1">
                                                                    <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">Qtd. (un)</span>
                                                                    <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">Peso (kg/km)</span>
                                                                    <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">Diâmetro (mm)</span>
                                                                    <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400" title="Quanto do vão o cabo realmente percorre">No vão (m)</span>
                                                                    <span className="sr-only">Ações</span>
                                                                </div>
                                                                <div className="space-y-2">
                                                                    {span.cables.map((cable, cableIndex) => (
                                                                        <div key={cable.id} className="grid grid-cols-2 sm:grid-cols-[repeat(4,minmax(0,1fr))_2rem] gap-2 items-end">
                                                                            {/* Labels only show on narrow screens — on sm+ the column header above carries them. */}
                                                                            <MiniInput
                                                                                label="Qtd."
                                                                                labelClassName="sm:hidden"
                                                                                min="1"
                                                                                step="1"
                                                                                value={cable.qty}
                                                                                onChange={(e) => updateCable(span.id, cable.id, { qty: e.target.value })}
                                                                            />
                                                                            <MiniInput
                                                                                label="Peso"
                                                                                labelClassName="sm:hidden"
                                                                                value={cable.weight}
                                                                                onChange={(e) => updateCable(span.id, cable.id, { weight: e.target.value })}
                                                                                placeholder={params.cableWeight}
                                                                            />
                                                                            <MiniInput
                                                                                label="Ø"
                                                                                labelClassName="sm:hidden"
                                                                                hint="Diâmetro do cabo — vazio usa o parâmetro global"
                                                                                value={cable.diameter}
                                                                                onChange={(e) => updateCable(span.id, cable.id, { diameter: e.target.value })}
                                                                                placeholder={params.cableDiameter}
                                                                            />
                                                                            <MiniInput
                                                                                label="No vão"
                                                                                labelClassName="sm:hidden"
                                                                                hint="Comprimento que o cabo percorre dentro do vão — vazio usa o vão inteiro"
                                                                                value={cable.spanLength}
                                                                                onChange={(e) => updateCable(span.id, cable.id, { spanLength: e.target.value })}
                                                                                placeholder={span.length || 'total'}
                                                                            />
                                                                            <div className="flex justify-end pb-1">
                                                                                {span.cables.length > 1 && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => removeCable(span.id, cable.id)}
                                                                                        className={iconButtonClass}
                                                                                        aria-label={`Remover cabo ${cableIndex + 1} do Vão ${index + 1}`}
                                                                                        title="Remover cabo"
                                                                                    >
                                                                                        <Trash2 className="h-4 w-4" />
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                <button type="button" onClick={() => addCable(span.id)} className={`${linkButtonClass} mt-2`}>
                                                                    <Plus className="h-3.5 w-3.5" />
                                                                    Adicionar cabo
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>

                                            <button
                                                type="button"
                                                onClick={addSpan}
                                                className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 text-sm font-medium text-slate-600 dark:text-slate-300 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800"
                                            >
                                                <Plus className="h-4 w-4" />
                                                Adicionar Vão
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="animate-fade-in-fast space-y-6">
                                            <StandardButtonGroup label="Vão (m)" options={[15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80]} selected={standardInput.span} onSelect={(val) => handleStandardInputChange('span', val)} gridClass="grid-cols-4 sm:grid-cols-7" />
                                            <StandardButtonGroup label="Altura do Poste (m)" options={[7, 9, 10, 11, 12, 13, 14, 15]} selected={standardInput.postHeight} onSelect={(val) => handleStandardInputChange('postHeight', val)} gridClass="grid-cols-4" />
                                            <StandardButtonGroup label="Quantidade de Cabos" options={[1, 2, 3, 4, 5, 6, 7, 8]} selected={standardInput.cableQty} onSelect={(val) => handleStandardInputChange('cableQty', val)} gridClass="grid-cols-4" />
                                            <div className="flex items-center pt-2">
                                                <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-slate-600 dark:text-slate-400">
                                                    <input
                                                        type="checkbox"
                                                        name="useMessenger"
                                                        checked={standardInput.useMessenger}
                                                        onChange={(e) => handleStandardInputChange('useMessenger', e.target.checked)}
                                                        className="h-5 w-5 rounded border-slate-300 accent-blue-600 focus:ring-blue-500"
                                                    />
                                                    Usar Cordoalha
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="md:col-span-4 flex flex-col justify-between md:border-l md:pl-6 border-slate-200 dark:border-slate-700">
                                    <div>
                                        {calcMode === 'digitado' && (
                                            <button onClick={calculateEffort} className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center gap-2 text-base">
                                                <Calculator className="h-5 w-5" />
                                                Calcular Esforço
                                            </button>
                                        )}
                                        {error && <div className="mt-4 text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/20 p-3 rounded-lg text-sm flex items-start gap-2"><Info className="h-5 w-5 shrink-0" /> {error}</div>}
                                    </div>
                                    <div className="mt-6 space-y-3">
                                        <ResultBox title="Componente Horizontal (H)" value={result} unit="kgf" field="H" onCopy={handleCopy} />
                                        <ResultBox title="Força Resultante no Ponto (Fr)" value={forceAtPoint} unit="kgf" field="Fr" onCopy={handleCopy} />
                                        {resultantAngle !== null && calcMode === 'digitado' && (
                                            <ResultBox title="Ângulo da Resultante (ref. Vão 1)" value={resultantAngle} unit="°" field="Angle" onCopy={handleCopy} />
                                        )}
                                        {calcMode === 'digitado' && breakdown.length > 1 && (
                                            <div className="bg-slate-100 dark:bg-slate-700/30 rounded-lg p-3">
                                                <h4 className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Contribuição por vão</h4>
                                                <ul className="space-y-1">
                                                    {breakdown.map(item => (
                                                        <li key={item.label} className="flex justify-between gap-2 text-xs text-slate-600 dark:text-slate-400">
                                                            <span className="whitespace-nowrap">{item.label} · {item.angleDeg.toFixed(0)}°</span>
                                                            <span className="font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">H {item.H.toFixed(0)} · Fr {item.Fr.toFixed(0)} kgf</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {activeTab === 'parametros' && (
                        <div id="tabpanel-parametros" role="tabpanel" aria-labelledby="tab-parametros" className="animate-fade-in-fast">
                            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-4">Parâmetros Padrão</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                                Salve os valores padrão aqui para agilizar seus cálculos na aba "Cálculo".
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <ParamInput
                                    label="Velocidade do Vento"
                                    unit="m/s"
                                    name="windSpeed"
                                    value={params.windSpeed}
                                    onChange={handleParamChange}
                                    icon={Wind}
                                    placeholder="Ex: 20"
                                />
                                <ParamInput
                                    label="Peso do Cabo"
                                    unit="kg/km"
                                    name="cableWeight"
                                    value={params.cableWeight}
                                    onChange={handleParamChange}
                                    icon={Weight}
                                    placeholder="Ex: 50"
                                />
                                <ParamInput
                                    label="Diâmetro do Cabo"
                                    unit="mm"
                                    name="cableDiameter"
                                    value={params.cableDiameter}
                                    onChange={handleParamChange}
                                    icon={Diameter}
                                    placeholder="Ex: 7"
                                />
                                <ParamInput
                                    label="Peso da Cordoalha"
                                    unit="kg/km"
                                    name="messengerWeight"
                                    value={params.messengerWeight}
                                    onChange={handleParamChange}
                                    icon={Weight}
                                    placeholder="Ex: 80"
                                />
                                <ParamInput
                                    label="Flecha"
                                    unit="%"
                                    name="arrow"
                                    value={params.arrow}
                                    onChange={handleParamChange}
                                    icon={ArrowDownUp}
                                    placeholder="Ex: 1"
                                />
                                <ParamInput
                                    label="Altura de Fixação dos Cabos"
                                    unit="m"
                                    name="attachmentHeight"
                                    value={params.attachmentHeight}
                                    onChange={handleParamChange}
                                    icon={Ruler}
                                    placeholder="Ex: 5.5"
                                />
                            </div>
                            <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
                                A altura de fixação é onde os cabos se prendem ao poste. A força resultante (Fr)
                                converte o momento gerado nessa altura para o ponto de esforço nominal do poste,
                                a 0,2 m do topo — por isso ela e a altura do poste andam juntas.
                            </p>
                            <div className="mt-8 flex items-center gap-4">
                                <button
                                    onClick={saveParams}
                                    className="bg-blue-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-700 transition-all duration-300 flex items-center gap-2 disabled:bg-slate-400"
                                >
                                    <Save className="h-5 w-5" />
                                    Salvar Parâmetros
                                </button>
                                {showSuccess && (
                                    <span className="text-green-600 dark:text-green-400 font-medium animate-fade-in">
                                        Parâmetros salvos com sucesso!
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default EffortCalculatorModal;
